import React, { useEffect, useState } from "react";
import { X, FileText, Loader2 } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { patchDocument } from "../../../api/elearning/documents/documents";
import { getSubjectsLookup } from "../../../api/elearning/subjects/subjectInfo";
import { getAllCoursesNoPaging } from "../../../api/elearning/courses/courses";
import { showSuccessToast, showErrorToast } from "../../../components/ConfirmDialog";

const TYPES = [
  { value: "giaotrinh", label: "Giáo trình" },
  { value: "baigiang", label: "Bài giảng" },
  { value: "baitap", label: "Bài tập" },
  { value: "video", label: "Video bài học" },
  { value: "dethi", label: "Đề kiểm tra" },
  { value: "khac", label: "Khác" },
];

const DocumentEditModal = ({ isOpen, document: doc, onClose, onSaved }) => {
  const [fileName, setFileName] = useState("");
  const [description, setDescription] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [subject, setSubject] = useState("");
  const [courseId, setCourseId] = useState("");
  const [docType, setDocType] = useState("");
  const [loading, setLoading] = useState(false);
  const [subjects, setSubjects] = useState([]);
  const [courses, setCourses] = useState([]);
  const [subjectsLoading, setSubjectsLoading] = useState(false);
  const [coursesLoading, setCoursesLoading] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    setSubjectsLoading(true);
    getSubjectsLookup()
      .then((res) => {
        if (cancelled) return;
        const items = Array.isArray(res) ? res : res?.items || [];
        setSubjects(items);
      })
      .catch(() => {
        if (!cancelled) setSubjects([]);
      })
      .finally(() => {
        if (!cancelled) setSubjectsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !subject) {
      setCourses([]);
      return;
    }
    const sub = subjects.find((s) => s.code === subject);
    if (!sub?.id) {
      setCourses([]);
      return;
    }
    let cancelled = false;
    setCoursesLoading(true);
    getAllCoursesNoPaging({ subject_id: sub.id, is_active: "true" })
      .then((data) => {
        if (cancelled) return;
        const raw = Array.isArray(data) ? data : data?.records ?? data?.items ?? [];
        setCourses(Array.isArray(raw) ? raw : []);
      })
      .catch(() => {
        if (!cancelled) setCourses([]);
      })
      .finally(() => {
        if (!cancelled) setCoursesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, subject, subjects]);

  useEffect(() => {
    if (!isOpen || !doc) return;
    setFileName(doc.file_name || "");
    setDescription(doc.description || "");
    setIsActive(doc.is_active !== false);
    setSubject(doc.subject || "");
    setCourseId(doc.course_id || "");
    setDocType(doc.type || "");
  }, [isOpen, doc]);

  const handleSave = async () => {
    if (!doc?.id) return;
    if (!fileName.trim()) return showErrorToast("Vui lòng nhập tên tài liệu.");
    if (!subject) return showErrorToast("Vui lòng chọn môn học.");
    setLoading(true);
    try {
      await patchDocument(doc.id, {
        file_name: fileName.trim(),
        description,
        is_active: isActive,
        subject,
        course_id: courseId || null,
        type: docType,
      });
      showSuccessToast("Đã cập nhật tài liệu.");
      onSaved?.();
      onClose?.();
    } catch (err) {
      showErrorToast(
        err.response?.data?.message || err.message || "Không thể cập nhật tài liệu."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && doc && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[110]"
            onClick={onClose}
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="fixed inset-0 z-[120] flex items-center justify-center p-4 pointer-events-none"
          >
            <div
              className="bg-white w-full max-w-lg rounded-3xl shadow-2xl overflow-hidden pointer-events-auto border border-gray-100"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50/80">
                <div className="flex items-center gap-3">
                  <FileText className="text-red-600" size={24} />
                  <div>
                    <h2 className="text-lg font-black text-gray-800 uppercase tracking-tight">
                      Sửa tài liệu
                    </h2>
                    <p className="text-xs text-gray-500 font-mono">{doc.code}</p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={onClose}
                  className="p-2 rounded-xl hover:bg-gray-100 text-gray-400"
                >
                  <X size={20} />
                </button>
              </div>

              <div className="p-6 space-y-4 max-h-[70vh] overflow-y-auto text-left">
                <div>
                  <label className="text-[10px] font-bold text-gray-400 uppercase">
                    Tên hiển thị
                  </label>
                  <input
                    type="text"
                    value={fileName}
                    onChange={(e) => setFileName(e.target.value)}
                    className="mt-1 w-full border-2 border-gray-100 rounded-xl px-4 py-2.5 focus:border-red-500 outline-none"
                  />
                </div>
                <div>
                  <label className="text-[10px] font-bold text-gray-400 uppercase">
                    Mô tả
                  </label>
                  <textarea
                    rows={3}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    className="mt-1 w-full border-2 border-gray-100 rounded-xl px-4 py-2.5 focus:border-red-500 outline-none"
                  />
                </div>
                <div className="grid grid-cols-1 gap-3">
                  <div>
                    <label className="text-[10px] font-bold text-gray-400 uppercase">
                      Môn học
                    </label>
                    <select
                      value={subject}
                      onChange={(e) => {
                        setSubject(e.target.value);
                        setCourseId("");
                      }}
                      disabled={subjectsLoading}
                      className="mt-1 w-full border-2 border-gray-100 rounded-xl px-3 py-2.5"
                    >
                      <option value="">— Chọn môn học —</option>
                      {subjects.map((s) => (
                        <option key={s.id || s.code} value={s.code}>
                          {s.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-[10px] font-bold text-gray-400 uppercase">
                      Khóa học (không bắt buộc)
                    </label>
                    <select
                      value={courseId}
                      onChange={(e) => setCourseId(e.target.value)}
                      disabled={coursesLoading || !subject}
                      className="mt-1 w-full border-2 border-gray-100 rounded-xl px-3 py-2.5"
                    >
                      <option value="">
                        {subject
                          ? "— Dùng chung môn học —"
                          : "Chọn môn học trước"}
                      </option>
                      {courses.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="text-[10px] font-bold text-gray-400 uppercase">
                    Loại tài liệu
                  </label>
                  <select
                    value={docType}
                    onChange={(e) => setDocType(e.target.value)}
                    className="mt-1 w-full border-2 border-gray-100 rounded-xl px-3 py-2.5"
                  >
                    {TYPES.map((s) => (
                      <option key={s.value} value={s.value}>
                        {s.label}
                      </option>
                    ))}
                  </select>
                </div>
                <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={isActive}
                    onChange={(e) => setIsActive(e.target.checked)}
                    className="rounded border-gray-300 text-red-600 focus:ring-red-500"
                  />
                  Đang sử dụng (hiển thị khi gán bài học)
                </label>
              </div>

              <div className="p-6 bg-gray-50 border-t flex justify-end gap-3">
                <button
                  type="button"
                  onClick={onClose}
                  disabled={loading}
                  className="px-5 py-2.5 rounded-xl font-bold text-gray-500 hover:bg-gray-200"
                >
                  Hủy
                </button>
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={loading}
                  className="px-6 py-2.5 rounded-xl font-bold bg-red-600 text-white hover:bg-red-700 flex items-center gap-2 disabled:opacity-50"
                >
                  {loading ? <Loader2 className="animate-spin" size={18} /> : null}
                  Lưu
                </button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default DocumentEditModal;
